/**
 * Brand styling for MCP server template cards. Each entry defines the hero
 * background and icon used in McpServerBrandPreview — distinct from the generic
 * workflow gradient used by ResourceTemplateCard.
 */

export const MCP_BRAND_STYLES = {
  github: {
    bg: "linear-gradient(145deg, #24292f 0%, #1b1f23 100%)",
    fg: "#ffffff",
    accent: "#238636",
  },
  linear: {
    bg: "linear-gradient(145deg, #5e6ad2 0%, #4b55a8 100%)",
    fg: "#ffffff",
    accent: "#b4b9ff",
  },
  supabase: {
    bg: "linear-gradient(145deg, #1c4332 0%, #0f2419 100%)",
    fg: "#ffffff",
    accent: "#3ecf8e",
  },
  stripe: {
    bg: "linear-gradient(145deg, #635bff 0%, #4f46e5 100%)",
    fg: "#ffffff",
    accent: "#a5b4fc",
  },
  cloudflare: {
    bg: "linear-gradient(145deg, #f6821f 0%, #f63 100%)",
    fg: "#ffffff",
    accent: "#fff7ed",
  },
  notion: {
    bg: "linear-gradient(145deg, #191919 0%, #000000 100%)",
    fg: "#ffffff",
    accent: "#ffffff",
  },
  slack: {
    bg: "linear-gradient(145deg, #611f69 0%, #4a154b 100%)",
    fg: "#ffffff",
    accent: "#ecb22e",
  },
  sentry: {
    bg: "linear-gradient(145deg, #362d59 0%, #1f1633 100%)",
    fg: "#ffffff",
    accent: "#f6b44c",
  },
  atlassian: {
    bg: "linear-gradient(145deg, #0052cc 0%, #0747a6 100%)",
    fg: "#ffffff",
    accent: "#79e2f2",
  },
  huggingface: {
    bg: "linear-gradient(145deg, #ff9d00 0%, #ff7a00 100%)",
    fg: "#ffffff",
    accent: "#fff3d6",
  },
  context7: {
    bg: "linear-gradient(145deg, #1f2937 0%, #0f172a 100%)",
    fg: "#ffffff",
    accent: "#94a3b8",
  },
  exa: {
    bg: "linear-gradient(145deg, #1d4ed8 0%, #1e3a8a 100%)",
    fg: "#ffffff",
    accent: "#bfdbfe",
  },
  firecrawl: {
    bg: "linear-gradient(145deg, #ea580c 0%, #b91c1c 100%)",
    fg: "#ffffff",
    accent: "#fed7aa",
  },
  tavily: {
    bg: "linear-gradient(145deg, #0ea5e9 0%, #0369a1 100%)",
    fg: "#ffffff",
    accent: "#bae6fd",
  },
  posthog: {
    bg: "linear-gradient(145deg, #f59e0b 0%, #111827 100%)",
    fg: "#ffffff",
    accent: "#fbbf24",
  },
  monday: {
    bg: "linear-gradient(145deg, #ff3d57 0%, #ffcb00 100%)",
    fg: "#ffffff",
    accent: "#fff3d6",
  },
  airtable: {
    bg: "linear-gradient(145deg, #2d7ff9 0%, #18bfff 100%)",
    fg: "#ffffff",
    accent: "#dbeafe",
  },
  zapier: {
    bg: "linear-gradient(145deg, #ff4f00 0%, #c2410c 100%)",
    fg: "#ffffff",
    accent: "#ffedd5",
  },
  neon: {
    bg: "linear-gradient(145deg, #00e599 0%, #047857 100%)",
    fg: "#ffffff",
    accent: "#d1fae5",
  },
  jina: {
    bg: "linear-gradient(145deg, #009191 0%, #004747 100%)",
    fg: "#ffffff",
    accent: "#99f6e4",
  },
  deepwiki: {
    bg: "linear-gradient(145deg, #6366f1 0%, #312e81 100%)",
    fg: "#ffffff",
    accent: "#c7d2fe",
  },
  langsmith: {
    bg: "linear-gradient(145deg, #1c3c3c 0%, #0f172a 100%)",
    fg: "#ffffff",
    accent: "#5eead4",
  },
  semgrep: {
    bg: "linear-gradient(145deg, #13bf95 0%, #0f766e 100%)",
    fg: "#ffffff",
    accent: "#ccfbf1",
  },
  apify: {
    bg: "linear-gradient(145deg, #246dff 0%, #1e40af 100%)",
    fg: "#ffffff",
    accent: "#bfdbfe",
  },
  browserbase: {
    bg: "linear-gradient(145deg, #111827 0%, #374151 100%)",
    fg: "#ffffff",
    accent: "#d1d5db",
  },
  search1api: {
    bg: "linear-gradient(145deg, #7c3aed 0%, #4c1d95 100%)",
    fg: "#ffffff",
    accent: "#ddd6fe",
  },
  datadog: {
    bg: "linear-gradient(145deg, #632ca6 0%, #4a148c 100%)",
    fg: "#ffffff",
    accent: "#e9d5ff",
  },
  honeycomb: {
    bg: "linear-gradient(145deg, #0291b6 0%, #0369a1 100%)",
    fg: "#ffffff",
    accent: "#bae6fd",
  },
  custom: {
    bg: "linear-gradient(145deg, #64748b 0%, #475569 100%)",
    fg: "#ffffff",
    accent: "#cbd5e1",
  },
};

/**
 * Brand icons — sources (all official or CC0; no hand-drawn placeholders):
 * - Simple Icons (CC0): github, linear, supabase, stripe, cloudflare, huggingface, posthog, airtable, zapier, neon, langsmith, datadog
 * - exa.ai brand logomark (hourglass, exa.ai/brand): exa
 * - @lobehub/lobe-icons: tavily, jina, search1api
 * - upstash/context7 (public/context7-icon.svg): context7
 * - firecrawl/open-scouts (public/brand/firecrawl-logo.svg): firecrawl
 * - monday.com brand mark (three capsules): monday
 * - semgrep/semgrep (images/semgrep-logo-light.svg, ellipsis mark): semgrep
 * - deepwiki.com/icon.png (site favicon; no public SVG): deepwiki
 * - apify.com/icon0.svg: apify
 * - browserbase.com/favicon.svg: browserbase
 * - docs.honeycomb.io/_assets/logos/HNY_Logomark_White_RGB.svg (hex cluster only): honeycomb
 */
export const MCP_BRAND_ICONS = {
  github: { src: "/svgs/mcp-brands/github.svg", mode: "mono" },
  linear: { src: "/svgs/mcp-brands/linear.svg", mode: "mono" },
  supabase: { src: "/svgs/mcp-brands/supabase.svg", mode: "mono" },
  stripe: { src: "/svgs/mcp-brands/stripe.svg", mode: "mono" },
  cloudflare: { src: "/svgs/mcp-brands/cloudflare.svg", mode: "mono" },
  notion: { src: "/svgs/mcp-brands/notion.svg", mode: "mono" },
  slack: { src: "/svgs/mcp-brands/slack-color.svg", mode: "color" },
  sentry: { src: "/svgs/mcp-brands/sentry.svg", mode: "mono" },
  atlassian: { src: "/svgs/mcp-brands/atlassian.svg", mode: "mono" },
  huggingface: { src: "/svgs/mcp-brands/huggingface.svg", mode: "mono" },
  context7: { src: "/svgs/mcp-brands/context7.svg", mode: "mono" },
  exa: { src: "/svgs/mcp-brands/exa.svg", mode: "mono" },
  firecrawl: { src: "/svgs/mcp-brands/firecrawl.svg", mode: "mono" },
  tavily: { src: "/svgs/mcp-brands/tavily.svg", mode: "mono" },
  posthog: { src: "/svgs/mcp-brands/posthog.svg", mode: "mono" },
  monday: { src: "/svgs/mcp-brands/monday.svg", mode: "mono" },
  airtable: { src: "/svgs/mcp-brands/airtable.svg", mode: "mono" },
  zapier: { src: "/svgs/mcp-brands/zapier.svg", mode: "mono" },
  neon: { src: "/svgs/mcp-brands/neon.svg", mode: "mono" },
  jina: { src: "/svgs/mcp-brands/jina.svg", mode: "mono" },
  deepwiki: { src: "/svgs/mcp-brands/deepwiki.png", mode: "mono" },
  langsmith: { src: "/svgs/mcp-brands/langsmith.svg", mode: "mono" },
  semgrep: { src: "/svgs/mcp-brands/semgrep.svg", mode: "mono" },
  apify: { src: "/svgs/mcp-brands/apify.svg", mode: "mono" },
  browserbase: { src: "/svgs/mcp-brands/browserbase.svg", mode: "mono" },
  search1api: { src: "/svgs/mcp-brands/search1api.svg", mode: "mono" },
  datadog: { src: "/svgs/mcp-brands/datadog.svg", mode: "mono" },
  honeycomb: { src: "/svgs/mcp-brands/honeycomb.svg", mode: "mono" },
  custom: { src: "/svgs/mcp-brands/custom.svg", mode: "mono" },
};

export function getMcpBrandStyle(brandId) {
  return MCP_BRAND_STYLES[brandId] || MCP_BRAND_STYLES.custom;
}

export function getMcpBrandIcon(brandId) {
  return MCP_BRAND_ICONS[brandId] || MCP_BRAND_ICONS.custom;
}

export function formatMcpCategory(category) {
  if (!category) return "";
  return category.charAt(0).toUpperCase() + category.slice(1);
}
